import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { errorMessage } from './client';

export interface Loadable<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Перечитать с сервера: после решения, после конфликта, после правки. */
  reload: () => void;
  /** Заменить данные локально, не ходя на сервер. */
  replace: (next: T) => void;
}

/** Каким запросом закончилась последняя загрузка и с какой ошибкой. */
interface Settled {
  request: string;
  error: string | null;
}

/** Имя загрузки → последний ответ и ключ зависимостей, к которым он относится. */
const keptLoads = new Map<string, { key: string; data: unknown }>();

function readKept<T>(keepAs: string | undefined, key: string): T | null {
  const saved = keepAs === undefined ? undefined : keptLoads.get(keepAs);
  return saved !== undefined && saved.key === key ? (saved.data as T) : null;
}

/** Ответы получены под прежней ролью: перезапуск сессии их забывает. */
export function forgetKeptLoads(): void {
  keptLoads.clear();
}

/**
 * Загрузка с перезапросом — одна на всё приложение.
 *
 * `loading` и `error` не ставятся в эффекте, а выводятся: запрос назван
 * строкой из зависимостей и счётчика перечитываний, и пока последний
 * отработавший запрос не совпал с текущим, идёт загрузка. Данные прошлого
 * запроса при этом остаются на экране — перечитывание не мигает пустотой.
 *
 * `enabled` — выключатель: пока ложен, запроса нет. Снова включили —
 * перечитываем.
 *
 * `keepAs` — имя в памяти возврата, как у `usePagedLoader`: экран рисует
 * прошлый ответ в первом же рендере — VKUI восстанавливает прокрутку именно
 * тогда — и перечитывает его в фоне. `loading` до ответа истинно: запомненное
 * сервер ещё не подтвердил. Помнится один ответ на имя.
 */
export function useLoader<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  enabled = true,
  keepAs?: string,
): Loadable<T> {
  const key = JSON.stringify(deps);
  const [data, setData] = useState<T | null>(() => readKept<T>(keepAs, key));
  const [settled, setSettled] = useState<Settled | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Функцию передают стрелкой прямо в вызов: в зависимостях она перезапускала
  // бы загрузку на каждый рендер. Держим последнюю в ref.
  const loadRef = useRef(load);
  useLayoutEffect(() => {
    loadRef.current = load;
  });

  const request = `${attempt}:${key}`;

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();

    void (async () => {
      try {
        const next = await loadRef.current(controller.signal);
        if (controller.signal.aborted) return;
        if (keepAs !== undefined) keptLoads.set(keepAs, { key, data: next });
        setData(next);
        setSettled({ request, error: null });
      } catch (cause: unknown) {
        if (controller.signal.aborted) return;
        setSettled({ request, error: errorMessage(cause) });
      }
    })();

    return () => controller.abort();
  }, [enabled, request, keepAs, key]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  const current = settled?.request === request ? settled : null;

  return {
    data,
    loading: enabled && current === null,
    error: current?.error ?? null,
    reload,
    replace: setData,
  };
}
